import { createSHA256 } from 'hash-wasm';
import { SUPPORTED_RE, contentTypeFor, isJunkPath, isZip } from './uploadPlan';

/* ---------------- folders (drag & drop) ---------------- */

function readEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  return new Promise((resolve) => reader.readEntries(resolve, () => resolve([])));
}

function entryFile(entry: FileSystemFileEntry): Promise<File | null> {
  return new Promise((resolve) => entry.file(resolve, () => resolve(null)));
}

async function walk(entry: FileSystemEntry, out: File[]): Promise<void> {
  if (isJunkPath(entry.fullPath || entry.name)) return;
  if (entry.isFile) {
    const f = await entryFile(entry as FileSystemFileEntry);
    if (f) out.push(f);
  } else if (entry.isDirectory) {
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    let batch: FileSystemEntry[];
    do {
      batch = await readEntries(reader);
      for (const e of batch) await walk(e, out);
    } while (batch.length);
  }
}

/** Every file in a drop, recursing into dropped folders. */
export async function filesFromDrop(dt: DataTransfer): Promise<File[]> {
  const entries = [...dt.items]
    .map((i) => (typeof i.webkitGetAsEntry === 'function' ? i.webkitGetAsEntry() : null))
    .filter((e): e is FileSystemEntry => !!e);
  if (!entries.length) return [...dt.files];
  const out: File[] = [];
  for (const e of entries) await walk(e, out);
  return out;
}

/* ---------------- ZIP archives ---------------- */

/** Unpack the photos inside a ZIP, in the browser. */
export async function unzipPhotos(zip: File): Promise<File[]> {
  const { ZipReader, BlobReader, BlobWriter } = await import('@zip.js/zip.js');
  const reader = new ZipReader(new BlobReader(zip));
  try {
    const entries = await reader.getEntries();
    const out: File[] = [];
    for (const e of entries) {
      if (e.directory || isJunkPath(e.filename)) continue;
      const base = e.filename.split('/').pop() ?? e.filename;
      if (!SUPPORTED_RE.test(base) || isZip(base)) continue;
      const blob = await e.getData!(new BlobWriter(contentTypeFor(base)));
      out.push(new File([blob], base, { type: contentTypeFor(base), lastModified: e.lastModDate?.getTime() ?? Date.now() }));
    }
    return out;
  } finally {
    await reader.close();
  }
}

/* ---------------- EXIF ---------------- */

const pad = (n: number, w = 2) => String(n).padStart(w, '0');

/**
 * EXIF dates are the camera's wall-clock time. exifr builds the Date from those fields in local time, so the local
 * getters give back the original wall-clock values. Encode them with a `Z` suffix (never converting through UTC),
 * the same way the server stores them, so the year agrees near New Year.
 */
export function wallClockIso(d: Date): string {
  return `${pad(d.getFullYear(), 4)}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}Z`;
}

/** EXIF capture time (DateTimeOriginal, else CreateDate) as wall-clock ISO, or undefined. */
export async function readTakenAt(file: File): Promise<string | undefined> {
  try {
    const exifr = (await import('exifr')).default;
    const tags = (await exifr.parse(file, { pick: ['DateTimeOriginal', 'CreateDate'] })) as
      | { DateTimeOriginal?: Date; CreateDate?: Date }
      | undefined;
    const d = tags?.DateTimeOriginal ?? tags?.CreateDate;
    return d instanceof Date && !Number.isNaN(d.getTime()) ? wallClockIso(d) : undefined;
  } catch {
    return undefined;
  }
}

/* ---------------- SHA-256 ---------------- */

const CHUNK = 8 * 1024 * 1024;
/** Files up to this size are hashed in one go with WebCrypto; bigger files are streamed in chunks. */
const ONE_SHOT_LIMIT = 128 * 1024 * 1024;

function hex(buf: ArrayBuffer): string {
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export async function sha256File(file: Blob): Promise<string> {
  if (file.size <= ONE_SHOT_LIMIT && crypto?.subtle) {
    return hex(await crypto.subtle.digest('SHA-256', await file.arrayBuffer()));
  }
  const h = await createSHA256();
  h.init();
  for (let off = 0; off < file.size; off += CHUNK) {
    h.update(new Uint8Array(await file.slice(off, off + CHUNK).arrayBuffer()));
  }
  return h.digest('hex');
}

/* ---------------- small concurrency helper ---------------- */

/** Run `fn` over `items` with at most `limit` in flight. */
export async function mapLimit<T>(items: readonly T[], limit: number, fn: (item: T, index: number) => Promise<void>) {
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next++;
      await fn(items[i]!, i);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
}
